import type Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import { formatDistance, geodesicArea, geodesicDistance, geodesicLength, geodesicPerimeter } from './measurement';

/**
 * V1.9.3 H · what the live card shows while something is being drawn. Pure functions over the geometry of the sketch:
 * every number comes from the geodesic engine in measurement.ts (the one the final measures use), never from pixels or
 * from the Web Mercator plane. Nothing here is stored in a project.
 */

export interface LivePolygon { readonly kind: 'polygon'; readonly areaM2: number; readonly perimeterM: number }
export interface LiveLine { readonly kind: 'line'; readonly totalM: number; readonly segmentM: number; readonly vertices: number }
export type LiveShape = LivePolygon | LiveLine;

/** The sketch geometry as the card reads it; null while there is nothing measurable yet. */
export function liveShape(geometry: Geometry | undefined | null): LiveShape | null {
  if (geometry instanceof Polygon || geometry instanceof MultiPolygon) {
    const flat = geometry instanceof Polygon ? geometry.getFlatCoordinates() : geometry.getFlatCoordinates();
    if (flat.length < 6) return null;
    return { kind: 'polygon', areaM2: geodesicArea(geometry), perimeterM: geodesicPerimeter(geometry) };
  }
  if (geometry instanceof LineString) {
    const coordinates = geometry.getCoordinates();
    if (coordinates.length < 2) return null;
    const segmentM = geodesicDistance(coordinates.at(-2)!, coordinates.at(-1)!);
    return { kind: 'line', totalM: geodesicLength(geometry), segmentM, vertices: coordinates.length };
  }
  return null;
}

/** Surface of the drawing once it is cut by the active geometry of its parent (Áreas parciales, Dividir estado de Suerte). */
export interface LiveEffective {
  readonly grossM2: number;
  readonly effectiveM2: number;
  readonly outsideM2: number;
  readonly parentM2: number;
  /** The parent minus the effective part: what stays outside the drawn part when a Suerte is split. */
  readonly restM2: number;
}

export type TargetState = 'none' | 'below' | 'near' | 'above';
export interface TargetReading { readonly state: TargetState; readonly diffHa: number; readonly text: string }

const hectares = (m2: number): string => (m2 / 10_000).toLocaleString('es-NI', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const liveHectares = (m2: number): string => `${hectares(m2)} ha`;
export const liveSquareMetres = (m2: number): string => `${m2.toLocaleString('es-NI', { maximumFractionDigits: m2 >= 1_000 ? 0 : 1, minimumFractionDigits: m2 >= 1_000 ? 0 : 1 })} m²`;
export const liveDistance = formatDistance;

/** «Cerca» is within 2 % of the target (at least 0.02 ha, i.e. two decimals of the hectare shown). */
export const NEAR_TARGET = { relative: 0.02, minimumHa: 0.02 } as const;

/**
 * The user's reference area against the drawn one (both in hectares, compared as shown: two decimals). It never stops
 * the drawing and never changes the geometry or the official measure.
 */
export function targetReading(areaM2: number, targetHa: number | null): TargetReading {
  if (targetHa === null || !Number.isFinite(targetHa) || targetHa <= 0) return { state: 'none', diffHa: 0, text: '' };
  const shown = Math.round(areaM2 / 100) / 100; const goal = Math.round(targetHa * 100) / 100;
  const diffHa = Math.round((goal - shown) * 100) / 100;
  const tolerance = Math.max(NEAR_TARGET.minimumHa, goal * NEAR_TARGET.relative);
  const state: TargetState = diffHa === 0 || Math.abs(diffHa) <= tolerance ? 'near' : diffHa > 0 ? 'below' : 'above';
  const text = diffHa === 0 ? 'objetivo alcanzado' : diffHa > 0 ? `faltan ${Math.abs(diffHa).toFixed(2)} ha` : `excede ${Math.abs(diffHa).toFixed(2)} ha`;
  return { state, diffHa, text };
}

/** Typed objective: «1.70», «1,7»; empty or invalid = no objective. */
export function parseTargetHa(text: string): number | null {
  const value = Number(text.trim().replace(',', '.'));
  return text.trim() && Number.isFinite(value) && value > 0 && value < 1_000_000 ? value : null;
}
